import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { API } from '../config';
import AddressCascade from './AddressCascade';
import staffTopoFetcher from './staffTopoFetcher';

// Aziende "Vicine" (Google Places) per lo STAFF: indirizzo autocompletato -> geocodifica (Nominatim/OSM,
// gratis) -> /staff-ai/vicine. Niente mappa: sul desktop l'indirizzo è più preciso e veloce.
const IconClose = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
);
const RAGGI = [500, 1000, 2000, 5000];

async function geocode(a) {
  const via = a.via && a.civico ? `${a.via} ${a.civico}` : a.via;
  const q = [via, a.comune, a.provincia, 'Italia'].filter(Boolean).join(', ');
  const r = await fetch(`https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&q=${encodeURIComponent(q)}`, { headers: { Accept: 'application/json' } });
  const j = await r.json();
  if (!j || !j.length) return null;
  return { lat: parseFloat(j[0].lat), lon: parseFloat(j[0].lon) };
}

export default function VicineDialog({ open, onClose }) {
  const [addr, setAddr] = useState({ provincia: '', comune: '', via: '', civico: '' });
  const [raggio, setRaggio] = useState(1000);
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState(null);
  const [next, setNext] = useState(null);
  const [pos, setPos] = useState(null);
  const [msg, setMsg] = useState('');
  const [aperto, setAperto] = useState(null);

  if (!open) return null;

  const cerca = async (p, page) => {
    try {
      const r = await axios.get(`${API}/staff-ai/vicine`, { params: { lat: p.lat, lon: p.lon, raggio, page: page || '' } });
      return r.data;
    } catch (e) { setMsg(e.response?.data?.detail || 'Ricerca non disponibile'); return null; }
  };

  const avvia = async () => {
    if (!addr.comune.trim() || !addr.via.trim()) return;
    setBusy(true); setItems(null); setNext(null); setAperto(null); setMsg('Individuo l\'indirizzo…');
    const p = await geocode(addr).catch(() => null);
    if (!p) { setBusy(false); setMsg('Indirizzo non individuato sulla mappa'); return; }
    setPos(p); setMsg('Cerco aziende vicine…');
    const d = await cerca(p);
    if (d) { setItems(d.items || []); setNext(d.next || null); setMsg((d.items || []).length ? '' : 'Nessuna azienda nel raggio scelto'); }
    setBusy(false);
  };

  const altre = async () => {
    if (!pos || !next) return;
    setBusy(true);
    const d = await cerca(pos, next);
    if (d) { setItems((prev) => [...(prev || []), ...(d.items || [])]); setNext(d.next || null); }
    setBusy(false);
  };

  const apri = async (it) => {
    setAperto({ ...it, loading: true });
    try { const d = (await axios.get(`${API}/staff-ai/vicine/${encodeURIComponent(it.id)}`)).data; setAperto({ ...it, ...d, loading: false }); }
    catch { setAperto({ ...it, loading: false }); }
  };

  const badge = (it) => {
    const a = it.archivio;
    if (!a || !a.trovato) return null;
    const col = a.risultato === 'NON TIM' ? '#22c55e' : a.risultato === 'TIM' ? '#e11d48' : '#64748b';
    return <span style={{ fontSize: 10.5, fontWeight: 800, color: '#fff', background: col, padding: '1px 7px', borderRadius: 999 }}>{a.risultato || 'in archivio'}</span>;
  };

  return createPortal(
    <div className="bulk-overlay" onClick={onClose}>
      <div className="bulk-dialog" onClick={(e) => e.stopPropagation()} style={{ width: 500 }}>
        <div className="bulk-content">
          <div className="bulk-head" style={{ marginBottom: 14 }}>
            <span className="bulk-title">Aziende vicine</span>
            <button className="bulk-x" onClick={onClose}><IconClose /></button>
          </div>

          <div style={{ opacity: busy ? 0.6 : 1, pointerEvents: busy ? 'none' : 'auto', marginBottom: 10 }}>
            <AddressCascade variant="desktop" inputClass="topo-in" fetcher={staffTopoFetcher} onChange={setAddr} />
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,.6)' }}>Raggio</span>
            {RAGGI.map((r) => (
              <button key={r} onClick={() => setRaggio(r)} disabled={busy}
                style={{ padding: '5px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: '1px solid ' + (raggio === r ? '#14b8a6' : 'rgba(255,255,255,.16)'), background: raggio === r ? 'rgba(20,184,166,.16)' : 'transparent', color: raggio === r ? '#5eead4' : 'rgba(255,255,255,.75)' }}>
                {r >= 1000 ? `${r / 1000} km` : `${r} m`}
              </button>
            ))}
            <button onClick={avvia} disabled={busy || !addr.comune.trim() || !addr.via.trim()}
              style={{ marginLeft: 'auto', padding: '8px 16px', borderRadius: 9, border: 0, fontWeight: 800, fontSize: 13, cursor: busy ? 'default' : 'pointer', opacity: busy || !addr.comune.trim() || !addr.via.trim() ? 0.6 : 1, color: '#fff', background: 'linear-gradient(180deg,#2dd4bf,#0d9488)' }}>
              {busy ? '…' : 'Cerca'}
            </button>
          </div>
          {msg && <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.6)', margin: '8px 0 0' }}>{msg}</div>}

          {items && items.length > 0 && (
            <div style={{ marginTop: 12, maxHeight: 340, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {items.map((it) => (
                <div key={it.id} onClick={() => apri(it)}
                  style={{ padding: '9px 11px', borderRadius: 10, border: '1px solid rgba(255,255,255,.1)', background: 'rgba(255,255,255,.04)', cursor: 'pointer' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: 13.5, color: '#fff', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.nome}</span>
                    {badge(it)}
                  </div>
                  {it.indirizzo && <div style={{ fontSize: 12, color: 'rgba(255,255,255,.6)', marginTop: 2 }}>{it.indirizzo}</div>}
                  {aperto && aperto.id === it.id && (
                    <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid rgba(255,255,255,.1)', fontSize: 12.5, color: 'rgba(255,255,255,.85)', display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {aperto.loading ? <span style={{ color: 'rgba(255,255,255,.5)' }}>Carico dettagli…</span> : (
                        <>
                          {aperto.telefono && <span><span style={{ color: 'rgba(255,255,255,.5)' }}>Tel</span> {aperto.telefono}</span>}
                          {aperto.sito && <span><span style={{ color: 'rgba(255,255,255,.5)' }}>Sito</span> <a href={aperto.sito} target="_blank" rel="noreferrer" style={{ color: '#5eead4' }}>{aperto.sito.replace(/^https?:\/\/(www\.)?/, '').slice(0, 40)}</a></span>}
                          {!aperto.telefono && !aperto.sito && <span style={{ color: 'rgba(255,255,255,.5)' }}>Nessun contatto pubblico</span>}
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {next && <button onClick={altre} disabled={busy} style={{ padding: '8px', borderRadius: 9, border: '1px solid rgba(255,255,255,.16)', background: 'rgba(255,255,255,.05)', color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Carica altre</button>}
            </div>
          )}

          {!items && !busy && !msg && (
            <div style={{ color: 'rgba(255,255,255,.55)', fontSize: 13, padding: '12px 0 0' }}>
              Inserisci un indirizzo e scegli il raggio: trovo le aziende vicine (Google Places) con lo stato in archivio.
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
